<template>
  <div
    class="SelectWrapper"
    :class="{ multiple, focus, error, disabled }"
  >
    <select
      :name="name"
      :value="isModelValueArray ? (modelValue as Array<unknown>)[0] : modelValue"
      :disabled="disabled"
      :multiple="multiple"
      class="Select"
      @change="handleChange"
      v-bind="$attrs"
      :aria-label="label"
    >
      <option
        v-for="(option, index) in internalOptions"
        :key="index"
        :value="option.value"
        :disabled="option.disabled"
        :selected="isSelected(option.value)"
        :class="{ selected: isSelected(option.value), placeholder: !!placeholder && index === 0 }"
      >
        {{ option.text }}
      </option>
    </select>
    <Caret
      v-if="!disabled && !multiple"
      class="SelectCaret"
    />
    <CaretDisabled
      v-if="disabled && !multiple"
      class="SelectCaret"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import type { SelectOption } from '.'
import { Caret, CaretDisabled } from '@ka-nabellinc/ka-nabell-icons'

export default defineComponent({
  components: {
    Caret,
    CaretDisabled,
  },
  props: {
    options: {
      type: Array as () => SelectOption[],
      required: true,
    },
    placeholder: {
      type: String,
      default: '選択してください',
    },
    emitNumberType: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number, Object as () => null, Array as () => (string | number | null)[]],
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    focus: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: null,
    },
    label: {
      type: String,
    }
  },
  emits: ['update:modelValue', 'click'],
  setup(props, { emit }) {
    const internalOptions = computed<SelectOption[]>(
      () =>  {
        if (!props.placeholder) {
          return props.options
        }

        return [
          {
            text: props.placeholder,
            value: '', 
            disabled: true,
          },
          ...props.options
        ]
      }
    )

    const isModelValueArray = computed(() => Array.isArray(props.modelValue))

    const isSelected = (val: number | string | null) => {
      if (isModelValueArray.value) {
        return (props.modelValue as Array<unknown>).map(String).includes(String(val))
      } else {
        return String(val) === String(props.modelValue)
      }
    }

    const handleChange = ({ target }: Event) => {
      if (!target) return
      if (props.multiple) {
        const options = (target as HTMLSelectElement).options
        const selected = Array.from(options)
          .filter((o) => o.selected)
          .map((o) => o.value)
        const isNumberExpected =
          props.emitNumberType ||
          (Array.isArray(props.modelValue) && props.modelValue.some((v) => typeof v === 'number'))
        if (isNumberExpected) {
          emit('update:modelValue', selected.map(Number))
        } else {
          emit('update:modelValue', selected)
        }
      } else {
        const { value } = target as HTMLSelectElement
        if (value === 'null') {
          emit('update:modelValue', null)
        } else if (props.emitNumberType || typeof props.modelValue === 'number') {
          emit('update:modelValue', Number(value))
        } else {
          emit('update:modelValue', value)
        }
      }
    }

    return {
      isModelValueArray,
      internalOptions,
      handleChange,
      isSelected,
    }
  },
})
</script>

<style scoped>
.SelectWrapper {
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;
  background: var(--primitive-colors-neutral-white-400);
  border-radius: 8px;
}

.SelectWrapper.disabled {
  cursor: not-allowed;
}

.Select {
  width: 100%;
  height: 38px;
  cursor: pointer;
  text-overflow: ellipsis;
  outline: none;
  border-radius: 8px;
  padding: 4px 26px 4px 16px;
  border: 1px solid var(--semantic-colors-border-box);
  background: transparent;
  box-sizing: border-box;
  color: var(--semantic-colors-text-black);
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.14px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  z-index: 1;
}

.Select:has(option.placeholder:checked) {
  color: var(--semantic-colors-text-placefolder);
}

.Select::-ms-expand {
  display: none;
}

.SelectWrapper.focus .Select,
.Select:focus {
  outline: 3px solid var(--primitive-colors-system-blue-200);
}

.SelectWrapper.error .Select {
  border: 1px solid var(--semantic-colors-border-error);
}

.Select:disabled {
  cursor: not-allowed;
  color: var(--semantic-colors-text-disabled);
}

.Select:disabled option {
  color: var(--semantic-colors-text-disabled);
}

.SelectWrapper.multiple {
  width: 100%;
  height: 100%;
  overflow: scroll;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 8px;
  border: 1px solid var(--semantic-colors-border-box);
  background: var(--primitive-colors-neutral-white-400);
  box-sizing: border-box;
}

.SelectWrapper.multiple.error {
  border: 1px solid var(--semantic-colors-border-error);  
}

.SelectWrapper.multiple.focus,
.SelectWrapper.multiple:has(.Select:focus) {
  outline: 3px solid var(--primitive-colors-system-blue-200);
}

.SelectWrapper.multiple .Select {
  height: 100%;
  width: 100%;
  border: none;
  outline: none;
  padding: 0 2px 0 8px;
}

.SelectWrapper.multiple option {
  color: var(--semantic-colors-text-black);
  font-size: var(--font-size-body-1);
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.14px;
  background: var(--primitive-colors-neutral-white-400);
}

.SelectWrapper.multiple:not(.disabled) option.placeholder {
  color: var(--semantic-colors-text-placefolder);
}

.SelectCaret {
  position: absolute;
  top: 10px;
  right: 8px;
  background: var(--primitive-colors-neutral-white-400);
}

.SelectWrapper.multiple option:checked,
.SelectWrapper.multiple .selected {
  background-color: var(--primitive-colors-system-blue-200);
}
</style>
